import { formatPercent } from "./formatter";

/**
 * Day-score aggregation shared by scoped cards + company leaderboard.
 *
 * Rate / average KPIs (Closed Rate, etc.) are period snapshots stored on day
 * rows as 0–1 fractions. Summing them across days (0.44 + 0.44 → 0.88) is how
 * YTD boards printed "1%" / "2%". Count and money KPIs still sum.
 *
 * For rates we keep the EARLIEST *meaningful* day in the window (callers must
 * feed days in chronological order). Empty placeholder days (amt 0) are
 * skipped so a Sunday startOf("week") cannot lock the week at 0%. Mid-month
 * recomputes that rewrite "today" with a partial basis still lose to an
 * earlier non-zero day — that pinned Team Monitoring Closed Rate at ~1%.
 */

/** Calcs that are period snapshots — replace across days, never sum. */
const RATE_LIKE_CALCS = new Set(["rate", "average", "avg", "latest", "bidaccuracy"]);

/** Calcs that are daily increments — must sum across days. */
const SUM_LIKE_CALCS = new Set(["sum", "memberships", "count"]);

/**
 * Composite KPIs (two source metrics combined via an operand, e.g.
 * Rev Cred Per Hr = Revenue Credit ÷ Hours Worked) are computed server-side
 * as a single calendar-month snapshot anchored to the month's first day
 * (see deriveWorkspaceCompositeScores / compositeRecompute on the server) —
 * never a per-day value. Same month-pinned-rate treatment AVG Ticket gets:
 * without it, Today/Yesterday/This Week/Last Week windows that don't happen
 * to include the 1st of the month read a hard 0 for the whole metric.
 */
export const isCompositeMeta = (meta) => {
  const opt = meta?.optional;
  return !!(opt?.sourceId1 && opt?.sourceId2 && opt?.operand);
};

export const isRateLikeCalc = (calc) => {
  if (calc == null || calc === "") return false;
  return RATE_LIKE_CALCS.has(String(calc).toLowerCase());
};

export const isSumLikeCalc = (calc) => {
  if (calc == null || calc === "") return false;
  return SUM_LIKE_CALCS.has(String(calc).toLowerCase());
};

/** Aggregate Bid Accuracy KPI (computed average, not the form). */
export const isBidAccuracyKpi = (meta) => {
  if (!meta) return false;
  if (String(meta?.parameters?.calc || meta?.calc || "").toLowerCase() === "bidaccuracy") {
    return true;
  }
  const t = `${meta?.title || meta?.name || ""}`.toLowerCase();
  return /\bquality bid accuracy\b|\bbid accuracy\b/.test(t);
};

/**
 * Percent display for a KPI's own value/target. Every percent-kind metric
 * rounds to a whole percent (43.14% → "43%") except Quality Bid Accuracy,
 * which always keeps one decimal (97.33% → "97.3%") — the one metric where a
 * whole number hides real movement players and admins both watch closely.
 * Used everywhere a KPI's "%"-kind amount is rendered: Team Performance vs.
 * The Yeti, Beat the Yeti, Scorekeeper, the leaderboards, dashboard widgets.
 */
export const formatKpiPercent = (meta, value) => {
  const n = Number(value) || 0;
  if (isBidAccuracyKpi(meta)) {
    return `${(n * 100).toFixed(1)}%`;
  }
  return formatPercent(Math.round(n * 100) / 100);
};

/** Day-cell / EmployeeKpi row tagged as Bid Accuracy calc. */
export const isBidAccuracyCell = (cell) => {
  if (!cell) return false;
  const calc = cell?.calc || cell?.metadata?.calc?.type;
  return String(calc || "").toLowerCase() === "bidaccuracy";
};

/**
 * Closed Rate / Bid Accuracy / AVG Ticket recompute for the active date
 * window via POST /kpi/window-rates, instead of copying the month-pinned
 * snapshot into every shorter window (which is why AVG Ticket used to read
 * the same number on Today/Yesterday/Week/Last Week no matter what).
 */
export const isWindowRateKpi = (meta, cell) => {
  if (isBidAccuracyKpi(meta) || isBidAccuracyCell(cell)) return true;
  const calc = meta?.calc || meta?.parameters?.calc || cell?.calc;
  const calcLower = String(calc || "").toLowerCase();
  if (calcLower === "rate" || calcLower === "average") return true;
  const title = `${meta?.title || meta?.name || ""}`.toLowerCase();
  if (/\bclose[d]?\s*rate\b|\bconversion\b|\bwin\s*rate\b|\bavg\.?\s*ticket\b|\baverage\s*ticket\b/.test(title)) return true;
  return false;
};

/**
 * Whether a KPI should keep a single period snapshot across days (not sum).
 * Explicit sum-like calcs (memberships, count, sum) always win over a stale
 * wholeMonth flag — that mis-tag was freezing Memberships at "1 of 125".
 */
export const isRateLikeKpi = (meta, cell) => {
  if (meta?.kind === "%" || cell?.kind === "%") return true;
  /* Bid Accuracy is a month snapshot (starts at 100%); summing day rows is how
     Scorekeeper printed 300% when three duplicate 100% rows existed. */
  if (isBidAccuracyKpi(meta) || isBidAccuracyCell(cell)) return true;
  /* Composites (Rev Cred Per Hr, etc.) are also a month-anchored snapshot —
     see isCompositeMeta. Today/Yesterday/Week/Last Week must fall back to the
     month value instead of reading 0 whenever the 1st sits outside the window. */
  if (isCompositeMeta(meta)) return true;
  const calc = meta?.calc || meta?.parameters?.calc || cell?.calc;
  if (isSumLikeCalc(calc)) return false;
  if (isRateLikeCalc(calc)) return true;
  const title = `${meta?.title || meta?.name || ""}`.toLowerCase();
  /* Stale wholeMonth on Memberships (pre-fix catalog) must not keep-latest. */
  if (/\bmemberships?\b/.test(title)) return false;
  /* Legacy wholeMonth only when calc is unknown — never override memberships. */
  if (meta?.wholeMonth || cell?.wholeMonth) return true;
  /* Catalog sometimes lacks kind/calc; rates/averages still must not be summed. */
  if (/\bclose[d]?\s*rate\b|\bconversion\b|\bwin\s*rate\b/.test(title)) return true;
  if (/\bavg\.?\s*ticket\b|\baverage\s*ticket\b|\bavg\.?\s*sale\b/.test(title)) return true;
  return false;
};

/**
 * Bid Accuracy starts each month at a perfect 100% until a form changes it.
 * Missing / placeholder zero in the window means "unscored" → 100% (1.0).
 * A real zero (form scored 0) is kept when the cell was actually written.
 * Values above 1 are duplicate-sum artifacts (three 100% rows → 300%) — clamp.
 */
export const effectiveMetricAmt = (meta, cell, { missing = false } = {}) => {
  const amt = Number(cell?.amt);
  if (!isBidAccuracyKpi(meta) && !isBidAccuracyCell(cell)) {
    return Number.isFinite(amt) ? amt : 0;
  }
  if (missing || cell == null) return 1;
  if (!Number.isFinite(amt) || amt <= 0) {
    /* Placeholder row with no real form score yet — default perfect month. */
    if (!cell?.scored) return 1;
  }
  return Math.min(amt, 1);
};

/**
 * Whether a player's cell counts in a TEAM AVERAGE divisor.
 * Unscored / placeholder zeros must not drag Closed Rate (etc.) toward 0% —
 * same rule as helpers/teamAverage.js. Genuine activity is amt > 0 (rates are
 * 0–1 fractions, so 2% = 0.02 still counts).
 */
export const countsTowardAverage = (cell) => {
  if (!cell || cell.amt == null) return false;
  return (Number(cell.amt) || 0) > 0;
};

/** Whether this cell counts as "scored" for averages and Yeti comparisons. */
export const metricCellScored = (meta, cell, { missing = false } = {}) => {
  if (missing || !cell) return isBidAccuracyKpi(meta);
  if (countsTowardAverage(cell)) return true;
  return isBidAccuracyKpi(meta);
};

/** Build { [kpiId]: def } from a workspace KPI list (string keys). */
export const metaByIdFromDefs = (defs) => {
  const map = {};
  (defs || []).forEach((d) => {
    if (!d || d._id == null) return;
    map[String(d._id)] = d;
  });
  return map;
};

/**
 * Fold one day's kpi map into an accumulator.
 * @param {object} into  { [kpiId]: { amt, pts } }
 * @param {object} dayKpis
 * @param {object} metaById
 * @param {{ addPts?: (n:number)=>void }} opts  addPts called only for sum metrics
 */
export const foldDayKpis = (into, dayKpis, metaById, opts = {}) => {
  Object.entries(dayKpis || {}).forEach(([kpiId, v]) => {
    if (kpiId === "total" || !v) return;
    const id = String(kpiId);
    const meta = (metaById && (metaById[id] || metaById[kpiId])) || null;
    const rate = isRateLikeKpi(meta, v);
    const amt = Number(v.amt) || 0;
    const pts = Number(v.pts) || 0;
    if (!into[id]) {
      into[id] = { amt, pts, scored: amt > 0 };
    } else if (rate) {
      /* Earliest *meaningful* day wins. Empty Sunday placeholders (amt 0) at
         startOf("week") must not lock Closed Rate / AVG Ticket at 0% for the
         whole week while weekday scores sit unused. */
      if ((Number(into[id].amt) || 0) <= 0 && amt > 0) {
        into[id] = { amt, pts, scored: true };
      }
    } else {
      into[id].amt += amt;
      into[id].pts += pts;
      if (amt > 0) into[id].scored = true;
    }
    if (!rate && typeof opts.addPts === "function") opts.addPts(pts);
  });
};

/** Sort day rows oldest → newest (invalid dates last). */
export const sortDaysAsc = (days) =>
  [...(days || [])].sort((a, b) => {
    const ta = new Date(a?.day).getTime();
    const tb = new Date(b?.day).getTime();
    const na = Number.isFinite(ta) ? ta : 0;
    const nb = Number.isFinite(tb) ? tb : 0;
    return na - nb;
  });

/** YYYY-MM-DD from a moment, Date, or ISO/date string (company-local when moment). */
const asDayKey = (v) => {
  if (v && typeof v.format === "function") return v.format("YYYY-MM-DD");
  const s = String(v || "");
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Composite KPIs (and any other month-pinned rate/average snapshot not
 * covered by isWindowRateKpi) are month-level rows stored on one
 * DailyScoreView day (usually YYYY-MM-01). Today / This week windows that
 * start after the 1st never see that row, so they printed 0. After folding
 * in-window days, pull any still-missing rate/average KPI from the same
 * calendar month(s) up through `to`.
 *
 * Closed Rate, Bid Accuracy, and AVG Ticket are NOT backfilled here — they
 * recompute for the active window via POST /kpi/window-rates (isWindowRateKpi).
 *
 * @param {object} into  accumulator from foldDayKpis
 * @param {Array} allDays  player's full day rows (not window-filtered)
 * @param {*} from
 * @param {*} to
 * @param {object} metaById
 */
export const backfillMonthPinnedRates = (into, allDays, from, to, metaById = null) => {
  if (!into || !metaById) return into;
  const toKey = asDayKey(to);
  const fromKey = asDayKey(from) || toKey;
  if (!toKey) return into;
  const fromMonth = fromKey.slice(0, 7);
  const toMonth = toKey.slice(0, 7);

  const candidates = sortDaysAsc(allDays).filter((day) => {
    const key = asDayKey(day?.day);
    if (!key || key > toKey) return false;
    const month = key.slice(0, 7);
    return month >= fromMonth && month <= toMonth;
  });

  Object.entries(metaById).forEach(([kpiId, meta]) => {
    if (!isRateLikeKpi(meta)) return;
    /* Window-based rates come from the API — never copy the month snapshot. */
    if (isWindowRateKpi(meta)) return;
    const id = String(kpiId);
    if ((Number(into[id]?.amt) || 0) > 0) return;

    for (let i = 0; i < candidates.length; i += 1) {
      const cell = candidates[i]?.kpis?.[id] || candidates[i]?.kpis?.[kpiId];
      if (!cell) continue;
      const amt = Number(cell.amt) || 0;
      if (amt <= 0) continue;
      into[id] = {
        amt,
        pts: Number(cell.pts) || 0,
        scored: true,
      };
      break;
    }
  });

  return into;
};
